import { useSuspenseQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { Suspense } from "react";

import { ModernBatchGrid } from "@/components/modern-batch-grid";
import { PageHeader } from "@/components/page-header";
import { Skeleton } from "@/components/ui/skeleton";
import { pb } from "@/lib/pb";
import { useDocumentTitle } from "@/lib/use-document-title";

interface BatchRecord {
  id: string;
  name: string;
  exam_year?: string;
  start_date?: string;
  end_date?: string;
}

interface TeacherRecord {
  id: string;
  name: string;
  batches: string[];
  login?: string;
}

function batchesQuery(authId: string | undefined) {
  return {
    queryKey: ["batches", "list", "page", authId ?? "anon"] as const,
    queryFn: () =>
      pb.collection("batch").getList<BatchRecord>(1, 24, {
        sort: "-created",
        skipTotal: true,
        fields: "id,name,exam_year,start_date,end_date",
      }),
  };
}

function teachersQuery(authId: string | undefined) {
  return {
    queryKey: ["teachers", "list", "page", authId ?? "anon"] as const,
    queryFn: () =>
      pb.collection("teacher").getList<TeacherRecord>(1, 50, {
        sort: "-created",
        skipTotal: true,
        fields: "id,name,batches,login",
      }),
  };
}

export const Route = createFileRoute("/_app/batches/")({
  loader: ({ context }) => {
    const authId = pb.authStore.model?.id;
    return Promise.all([
      context.queryClient.ensureQueryData(batchesQuery(authId)),
      context.queryClient.ensureQueryData(teachersQuery(authId)),
    ]);
  },
  component: BatchesPage,
});

function BatchesPage() {
  useDocumentTitle("Batches");
  return (
    <>
      <PageHeader
        eyebrow="Cohorts"
        title="Batches"
        meta="Browse cohorts, faculty and active timelines."
      />
      <Suspense fallback={<GridSkeleton />}>
        <Grid />
      </Suspense>
    </>
  );
}

function Grid() {
  const authId = pb.authStore.model?.id;
  const { data: batches } = useSuspenseQuery(batchesQuery(authId));
  const { data: teachers } = useSuspenseQuery(teachersQuery(authId));
  return <ModernBatchGrid batches={batches.items} teachers={teachers.items} />;
}

function GridSkeleton() {
  return (
    <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
      {Array.from({ length: 6 }).map((_, i) => (
        // biome-ignore lint/suspicious/noArrayIndexKey: stable skeleton placeholder
        <Skeleton key={i} className="h-44 w-full rounded-2xl" />
      ))}
    </div>
  );
}
